/**
 * nebula_look — which nebulae a star system has, of what KIND, and how they are drawn (space realism, 2026-10-07). PURE.
 *
 * The engine draws a nebula from a `NebulaSpec` (enginex/render/space/nebula): five physical kinds, each baked once
 * into a small volume and marched. This file is Atlas's side of it:
 *   - THE SITES. The sector's three nebula points of interest each ARE a kind — The Veil is a supernova remnant (thin
 *     filaments), Beacon Verge a planetary nebula (a ring around its dying star, minutes from the launch ring), The
 *     Murk a dark cloud against glowing gas — and two more stand far outside the lanes as the sky's landmarks: an
 *     emission nebula (a star-forming cloud) and a reflection nebula (dust around young blue stars). Other star systems
 *     get at most one distant nebula, by their id alone.
 *   - THE QUALITY. The `nebulae` graphics flag picks the volume's level, never whether a nebula exists: off = `low`
 *     (a 48³ volume, 20 steps, no wisps — cheap enough for every tier), on = `high` (96³, 40 steps, wisps), the ultra
 *     tier `ultra`. One nebula is the same nebula at every level.
 *   - THE SWITCH. `localStorage atlas:nebula` = 'legacy' (the old glow volume + sprite puffs) | 'off'.
 * Colours and ratios are calibrated against photographs — atlas/docs/research/2026-10-07-space-realism/.
 */
import { nebulaPreset, type NebulaKind, type NebulaSpec } from 'enginex/render/space/nebula/NebulaField.js';

export interface NebulaSite {
  /** Stable id (a POI's key, or `sky:<n>`). */
  key: string;
  name: string;
  kind: NebulaKind;
  seed: number;
  /** Absolute sector metres. */
  pos: [number, number, number];
  /** The nebula's radius (m) — the volume's bounding sphere. */
  radius: number;
  /** A landmark far outside the lanes (never a destination). */
  sky: boolean;
}

/** What each of the sector's nebula POIs is. A nebula POI not listed takes a kind from its key. */
export const NEBULA_POI_KIND: Readonly<Record<string, NebulaKind>> = { poi5: 'remnant', poi15: 'planetary', poi23: 'dark' };
/** A POI's `radius` is its play volume; the nebula around it reaches a little farther. */
export const NEBULA_POI_REACH = 1.15;

/** The Commons Reach's two sky landmarks (absolute metres; far outside the ±1,500 km the lanes use). */
export const SKY_NEBULAE_SYS0: ReadonlyArray<NebulaSite> = [
  { key: 'sky:0', name: 'The Ember Cradle', kind: 'emission', seed: 4107, pos: [2_050_000, 520_000, 1_750_000], radius: 520_000, sky: true },
  { key: 'sky:1', name: 'The Pale Sisters', kind: 'reflection', seed: 2291, pos: [-2_250_000, -380_000, 1_500_000], radius: 360_000, sky: true },
];

function fnv(s: string): number { let h = 0x811c9dc5; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); } return h >>> 0; }
/** fnv + the murmur3 finalizer: bare FNV-1a is arithmetic in its last character (THE HASH LAW, lib/galaxy.ts). */
function mix01(s: string): number {
  let h = fnv(s);
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

const KIND_BY_ROLL: ReadonlyArray<[number, NebulaKind]> = [[0.34, 'emission'], [0.56, 'reflection'], [0.76, 'remnant'], [0.88, 'planetary'], [1.01, 'dark']];
/** Share of derived star systems that have a distant nebula in their sky. */
export const SKY_NEBULA_SHARE = 0.45;

/** The distant nebulae of a star system's sky (sys0: the two landmarks; any other system: none or one, by its id). */
export function skyNebulaeFor(systemId: string, named?: NebulaKind | null): NebulaSite[] {
  if (systemId === 'sys0') return SKY_NEBULAE_SYS0.map((s) => ({ ...s, pos: [...s.pos] as [number, number, number] }));
  // natural objects (2026-10-07): a showpiece system NAMES its nebula (a pulsar its remnant, a white dwarf its planetary
  // nebula, an infant star its dark cloud) or says it has none (null); undefined = the share and the roll below.
  if (named === null) return [];
  if (named === undefined && mix01(`${systemId}:neb`) >= SKY_NEBULA_SHARE) return [];
  const roll = mix01(`${systemId}:nebk`);
  const kind = named ?? KIND_BY_ROLL.find(([p]) => roll < p)![1];
  const az = mix01(`${systemId}:neba`) * Math.PI * 2, el = (mix01(`${systemId}:nebe`) - 0.5) * 0.9;
  const dist = 2_300_000 + mix01(`${systemId}:nebd`) * 500_000;
  // a compact kind (a planetary nebula, a dark globule) is small in a real sky too
  const radius = (kind === 'planetary' ? 150_000 : kind === 'dark' ? 300_000 : 380_000) * (0.8 + 0.5 * mix01(`${systemId}:nebr`));
  return [{
    key: 'sky:0', name: 'distant nebula', kind, seed: fnv(`${systemId}:nebs`) % 100_000,
    pos: [Math.cos(az) * Math.cos(el) * dist, Math.sin(el) * dist, Math.sin(az) * Math.cos(el) * dist], radius, sky: true,
  }];
}

/** Every nebula of a system: its nebula POIs (as the kinds they are) and its sky landmarks. */
export function nebulaSitesFor(systemId: string, pois: ReadonlyArray<{ key: string; name: string; kind: string; pos: [number, number, number]; radius: number }>, named?: NebulaKind | null): NebulaSite[] {
  const out: NebulaSite[] = [];
  for (const p of pois) {
    if (p.kind !== 'nebula') continue;
    const kind = NEBULA_POI_KIND[p.key] ?? KIND_BY_ROLL.find(([q]) => mix01(`${p.key}:nebk`) < q)![1];
    out.push({ key: p.key, name: p.name, kind, seed: fnv(`${systemId}:${p.key}`) % 100_000, pos: [...p.pos] as [number, number, number], radius: p.radius * NEBULA_POI_REACH, sky: false });
  }
  return out.concat(skyNebulaeFor(systemId, named));
}

/** The spec a kind and seed are drawn from: the engine's preset under Atlas's calibration. */
export function nebulaSpecFor(kind: NebulaKind, seed: number): NebulaSpec {
  return nebulaPreset(kind, seed);
}

export type NebulaLevel = 'low' | 'high' | 'ultra';
/** The `nebulae` flag picks the level; a nebula exists on every tier. */
export function nebulaLevel(flagOn: boolean, ultraTier: boolean): NebulaLevel {
  return flagOn ? (ultraTier ? 'ultra' : 'high') : 'low';
}

export type NebulaMode = 'volume' | 'legacy' | 'off';
export function nebulaModeOf(stored: string | null | undefined): NebulaMode {
  return stored === 'legacy' ? 'legacy' : stored === 'off' ? 'off' : 'volume';
}

/** Bake order: what the pilot is nearest to (by angular size) first. */
export function nebulaBakeOrder(sites: ReadonlyArray<NebulaSite>, from: [number, number, number]): NebulaSite[] {
  const size = (s: NebulaSite): number => s.radius / Math.max(s.radius * 0.5, Math.hypot(s.pos[0] - from[0], s.pos[1] - from[1], s.pos[2] - from[2]));
  return [...sites].sort((a, b) => size(b) - size(a));
}
